// Profilom (slice 4.5): every signed-in user's own name and password. The
// address and the role are shown, not edited — an admin changes a role on the
// Beállítások page. A new password needs the current one, and ends every
// other session the user had; this one continues on the token the answer
// carries.

import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { toast } from "sonner";
import { changeOwnPassword, updateProfile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DetailList } from "@/components/emc/DetailList";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { ErrorState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { messageOf } from "@/pages/campaigns/model";
import { PASSWORD_MIN, ROLE_LABEL, passwordProblem, repeatProblem } from "@/pages/users/model";

function NameCard() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.full_name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();

  useEffect(() => setName(user?.full_name ?? ""), [user?.full_name]);
  if (user === null) return null;
  const unchanged = name.trim() === user.full_name.trim();

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await updateProfile(name.trim());
      await refresh();
      toast.success("A név elmentve");
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Adataim</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <DetailList
          items={[
            { label: "Email-cím", value: user.email },
            { label: "Szerep", value: ROLE_LABEL[user.role] ?? user.role },
            { label: "Utolsó belépés", value: minute(user.last_login_at) },
          ]}
        />
        <form className="flex flex-col gap-3" onSubmit={(event) => void save(event)}>
          <Field label="Név" htmlFor={nameId}>
            <Input id={nameId} required maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          {error !== null && <ErrorState message={error} />}
          <div>
            <Button type="submit" disabled={busy || unchanged || name.trim() === ""}>
              {busy ? "Mentés…" : "Név mentése"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordCard() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentId = useId();
  const nextId = useId();
  const againId = useId();
  if (user === null) return null;

  const policy = next === "" ? null : passwordProblem(next, user.email);
  const mismatch = repeatProblem(next, again);
  const ready = current !== "" && next !== "" && again !== "" && policy === null && mismatch === null;

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!ready || user === null) return;
    setBusy(true);
    setError(null);
    try {
      await changeOwnPassword(current, next);
      setCurrent("");
      setNext("");
      setAgain("");
      toast.success("A jelszó megváltozott", {
        description: "A többi bejelentkezésed véget ért; ez folytatódik.",
      });
    } catch (raised: unknown) {
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Jelszócsere</CardTitle>
        <CardDescription>
          Legalább {PASSWORD_MIN} karakter, és nem lehet az email-címed. Csere után minden más
          bejelentkezésed megszűnik.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={(event) => void save(event)}>
          {/* For the browser's password manager: whose password this is. */}
          <input type="email" autoComplete="username" value={user.email} readOnly hidden />
          <Field label="Jelenlegi jelszó" htmlFor={currentId}>
            <Input
              id={currentId}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
          <Field label="Új jelszó" htmlFor={nextId} hint={policy ?? undefined}>
            <Input
              id={nextId}
              type="password"
              autoComplete="new-password"
              aria-invalid={policy !== null || undefined}
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>
          <Field label="Új jelszó még egyszer" htmlFor={againId} hint={mismatch ?? undefined}>
            <Input
              id={againId}
              type="password"
              autoComplete="new-password"
              aria-invalid={mismatch !== null || undefined}
              value={again}
              onChange={(event) => setAgain(event.target.value)}
            />
          </Field>
          {error !== null && <ErrorState message={error} />}
          <div>
            <Button type="submit" disabled={busy || !ready}>
              {busy ? "Folyamatban…" : "Jelszó cseréje"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default function Profile() {
  return (
    <>
      <PageHeader title="Profilom" description="A neved és a jelszavad. A szerepedet egy admin állítja a Beállításokban." />
      <div className="grid gap-6 lg:grid-cols-2">
        <NameCard />
        <PasswordCard />
      </div>
    </>
  );
}
